// English content dictionary. All user-facing EN text lives here so pages stay
// presentation-only and translations are easy to maintain.

const en = {
  meta: {
    home: {
      title: "Find the best place to drill a water well",
      description:
        "Expert groundwater location services. Save thousands of dollars by finding the perfect spot to drill your water well!",
    },
    services: {
      title: "Groundwater Location & Geophysical Services",
      description:
        "Explore AquaFinders services: finding water, avoiding water and fractures, soil resistivity testing, and geophysical studies for residential, agricultural and commercial projects.",
    },
    findWater: {
      title: "Find Water - Locate the Best Well Drilling Spot",
      description:
        "Non-invasive geophysical surveys that pinpoint the best location to drill a productive water well and reduce the risk of dry wells.",
    },
    avoidWater: {
      title: "Avoid Water & Fractures Before You Dig",
      description:
        "Identify underground water and fractures before drilling deep foundations, footings or infrastructure with AquaFinders geophysical surveys.",
    },
    resistivity: {
      title: "Soil Resistivity Testing for Grounding & Environmental Projects",
      description:
        "Electrical soil resistivity studies for power stations, substations, landfills and industrial grounding systems, plus contamination assessment.",
    },
    studies: {
      title: "Geophysical, Mining & Environmental Studies",
      description:
        "Geological, hydrogeological and resistivity studies to support environmental, construction and mining projects.",
    },
    process: {
      title: "Process & Pricing for Water Well Location",
      description:
        "Understand AquaFinders' step-by-step process and transparent pricing for geological studies and Electrical Resistivity Imaging (ERI) surveys.",
    },
    about: {
      title: "About AquaFinders - Groundwater Location Experts",
      description:
        "A specialized team of geologists and hydrogeologists using cutting-edge technology to locate the best spots for productive water wells.",
    },
    contact: {
      title: "Contact AquaFinders",
      description:
        "Get in touch with AquaFinders to find your perfect well location. We speak English and Spanish. Call, email or send us a message.",
    },
    faq: {
      title: "Frequently Asked Questions",
      description:
        "Find answers to common questions about AquaFinders water well location services, costs, process, guarantees and study depth.",
    },
    gallery: {
      title: "Image Gallery - Resistivity Imaging & Field Work",
      description:
        "A gallery of AquaFinders resistivity imaging results and field survey work.",
    },
    request: {
      title: "Start Now - Initial Contact",
      description:
        "Please let us know about your project and we will contact you to discuss the next steps.",
    },
    success: {
      title: "Thank You for Contacting Us",
      description: "Your message has been successfully submitted.",
    },
    sitemap: {
      title: "Site Map",
      description: "AquaFinders site map - find all of our pages in one place.",
    },
  },

  htmlLang: "en-US",

  nav: {
    services: "Services",
    pricing: "Pricing",
    about: "About",
    start: "Start now",
    faq: "FAQ",
    contact: "Contact",
    otherLanguage: "Spanish Version",
  },

  langSwitch: { enLabel: "English Language", esLabel: "Spanish Language" },

  common: {
    weAreInsured: "We are insured!",
    startNow: "Start now",
    freeConsultation: "Free Consultation",
    imageGallery: "Image Gallery",
    sampleResistivity: "Sample a resistivity imaging",
    infoProcess: "Click here to learn more information about our process...",
    skipToContent: "Skip to main content",
    home: "Home",
  },

  hero: {
    title: "Find the best place to drill your water well!",
    description:
      "Dry and low-yield wells pose significant risks, potentially impacting your budget and delaying construction! At AquaFinders, we combine cutting-edge subsurface imaging technology with expert geological analysis to locate hidden groundwater beneath your site. We'll help you confidently select the optimal location for drilling, significantly improving your chances of a productive well that will benefit you for decades to come!",
    audioSrc: "/podcast.mp4",
    audioLabel: "AquaFinders introduction podcast",
  },

  explanationLead: "We provide ",
  explanationStrong: "YOUR",
  explanationTail:
    " well drillers and builders expert advice on the best places to dig.",

  services: {
    heading: "Our Services",
    cards: {
      findWater: {
        title: "Finding water",
        description:
          "We use advanced scientific techniques to identify prime locations for well drilling - residential, commercial, and agriculture.",
      },
      avoidWater: {
        title: "Avoiding water",
        description:
          "Sometimes you want to avoid water, like when digging deeper foundations, gas reservoirs, landfills, and more. We can help with that too.",
      },
      resistivity: {
        title: "Soil resistivity",
        description:
          "Test required for grounding projects for substations and transmission systems, solar farms, industrial plants, etc.",
      },
      studies: {
        title: "Studies",
        description:
          "Geophysical and hydrogeophysical studies of your property, to assist in environmental, construction, and mining work.",
      },
    },
  },

  whoHires: {
    heading: "Who hires us?",
    items: [
      {
        title: "Residential",
        description:
          "Homeowners planning to drill water wells on new construction or existing sites.",
      },
      {
        title: "Agriculture",
        description:
          "Farmers seeking the best places to drill high-yield water wells for crop irrigation or animal consumption.",
      },
      {
        title: "Business",
        description:
          "Builders seeking to study the grown beneath their property, in order to avoid water and fractures.",
      },
    ],
  },

  about: {
    heading: "About Us",
    paragraphs: [
      "AquaFinders is a specialized groundwater location service operating primarily in North Carolina, South Carolina, Georgia, Tennessee, and Kentucky, with nationwide service available upon special request. Our team of five experienced geologists/hydrogeologists, a forest engineer, an agriculture engineer, and local technicians, uses cutting-edge technology to locate the best spots for drilling productive water wells.",
      "Founded on the principle that everyone deserves access to clean, reliable water, our experts have helped hundreds of homeowners, farmers, and businesses avoid the costly mistake of drilling in the wrong location.",
      "Our scientific approach combines hydrogeological analysis with geophysical imaging—also known as Electrical Resistivity Imaging (ERI) or Electrical Resistivity Tomography (ERT)—to maximize your chances of finding water and pinpoint the areas on your site with the highest probability of strong water flow.",
    ],
    closing: ["Your future is better with water!", "Contact AquaFinders for peace of mind!"],
    imageAlt: "AquaFinders team conducting a groundwater survey in the field",
  },

  servicePages: {
    findWater: {
      heading: "Discover Water with Confidence",
      body: [
        "At AquaFinders, we specialize in non-invasive geophysical surveys to pinpoint the best locations for drilling wells. Using a combination of advanced techniques — including satellite imagery analysis, topographic assessment, hydrogeological research, and on-site electrical resistivity studies — we deliver a clear, detailed understanding of your property's subsurface conditions. Our science-backed approach dramatically reduces the risk of drilling dry wells, saving you time, money, and resources.",
        "Our geophysical surveys are designed to be cost-effective and environmentally friendly. By using non-invasive techniques, we minimize the impact on the surrounding environment while providing accurate and reliable data for well drilling. Trust AquaFinders to help you locate water more efficiently, with greater accuracy and peace of mind.",
      ],
    },
    avoidWater: {
      heading: "Avoiding fractures and water",
      body: [
        "At AquaFinders, we can also help you avoid drilling in areas with fractures or water. Our non-invasive geophysical surveys are designed to identify potential issues before you start drilling, ensuring that you can make informed decisions. Using a combination of advanced techniques — including satellite imagery analysis, topographic assessment, hydrogeological research, and on-site electrical resistivity studies — we deliver a clear, detailed understanding of your property's subsurface conditions. Our science-backed approach dramatically reduces the risk of drilling dry wells, saving you time, money, and resources.",
        "Builders and contractors can benefit from our services by avoiding costly mistakes and ensuring that their projects are completed on time and within budget. Our geophysical surveys are designed to be cost-effective and environmentally friendly. By using non-invasive techniques, we minimize the impact on the surrounding environment while providing accurate and reliable data. Trust AquaFinders to help you dig confidently, use your resources more efficiently, and achieve peace of mind.",
      ],
    },
    resistivity: {
      heading: "Soil Resistivity",
      body: [
        "Our Soil Resistivity studies are often necessary for power stations, landfills, and other projects that requires this type of environmental studies. It can also be used to evaluate possible spills and contamination in the soil.",
        "We use the latest technology to provide you with the best results. Our team of experts will conduct a thorough analysis of your site, using advanced techniques to measure the resistivity of the soil. This information is crucial for understanding the subsurface conditions of your site, including possible chemical infiltration that already happened or the possibility of future contamination of waterbeds.",
      ],
    },
    studies: {
      heading: "Mining and Environmental Studies",
      body: [
        "Many endeavors require a thorough understanding of the subsurface conditions. Our team can perform a range of studies, including geological, hydrogeological, and resistivity, which are often necessary for mining and environmental projects.",
        "Contact us to learn more about our services and how we can help you with your project.",
      ],
    },
  },

  servicesList: {
    heading: "Services",
    sampleHeading: "Sample of resistivity imaging",
    sampleDescription:
      "The resistivity image displays a cross-sectional view of the subsurface along a straight line. Low resistivity zones (warmer colors like red and yellow) may indicate moist soil or aquifers, while high resistivity zones (cooler colors like blue and green) often represent rock, dry soil, or impermeable layers.",
    cards: [
      {
        key: "findWater",
        title: "Find Water",
        subtitle: "Find the Best Location for Your Water Well",
        items: [
          "Homeowners looking to find the best place to drill a well and maximize their investment",
          "Homesteaders aiming to optimize their land's water potential for farming, gardening, and livestock",
          "Land buyers to be confident that the land they are purchasing can produce the water they need",
          "Well drilling companies aiming to increase their success rate and customer satisfaction",
          "Builders seeking to minimize construction delays and reduce unpredictable expenses",
          "Farmers needing a reliable water source for irrigation and livestock operations",
          "Developers planning subdivisions or commercial projects that require multiple wells",
          "Landowners preparing for future construction, farming, or increasing resale value",
        ],
      },
      {
        key: "avoidWater",
        title: "Avoid Water",
        subtitle:
          "Avoid Underground Fractures and Unexpected Water During Construction",
        items: [
          "Builders needing to avoid costly surprises like underground fractures, caves, or unexpected water during foundation work",
          "Engineers planning deep footings, retaining walls, or basements, ensuring soil and rock stability",
          "Contractors installing large poles, towers, or fences requiring safe, dry drilling conditions",
          "Land developers aiming to prevent delays and minimize construction risks",
          "Homeowners planning pools or major landscaping projects where subsurface conditions matter",
          "Utility companies and municipalities installing underground pipelines, cables, or infrastructure",
        ],
      },
      {
        key: "resistivity",
        title: "Soil Resistivity",
        subtitle: "Soil Resistivity Studies for Safer and Smarter Projects",
        items: [
          "Engineers and contractors needing soil resistivity data for power stations, substations, and grounding system designs",
          "Environmental consultants assessing landfills, waste disposal sites, or hazardous material storage areas",
          "Project managers overseeing industrial developments where spill risks or soil contamination are concerns",
          "Landowners investigating possible soil contamination before purchasing or developing property",
          "Companies evaluating chemical infiltration risks that could threaten waterbeds and groundwater sources",
          "Municipalities conducting environmental impact studies for infrastructure expansion",
        ],
      },
      {
        key: "studies",
        title: "Mining & Environment",
        subtitle:
          "Mining and Environmental Studies for Safer and Smarter Development",
        items: [
          "Mining companies needing detailed geological and hydrogeological studies before exploration or excavation",
          "Environmental consultants conducting site assessments, impact studies, or land restoration projects",
          "Developers seeking to understand subsurface risks before investing in large-scale projects",
          "Landowners evaluating the environmental safety and resource potential of their property",
          "Government agencies requiring site evaluations for compliance, remediation, or conservation efforts",
          "Engineers planning tunnels, quarries, or earthworks where subsurface stability is critical",
        ],
      },
    ],
  },

  process: {
    title: "Process and Pricing",
    subtitle: "For finding the best place to drill a water well",
    intro: "Our services are divided into two parts:",
    prices: [
      {
        amount: "$150",
        muted: true,
        title: "(Optional) Hydrogeological Study",
        description:
          "This optional preliminary hydrogeological study analyses satellite imagery to evaluate the water potential of your property. Its purpose is to help you better understand your land and determine whether further studies are recommended or if there is already a clear path for your water well placement. In other words, it’s ideal for those who are unsure whether they need the more advanced studies described below. If you choose to proceed with our full Geological Study, the cost of this preliminary study will be deducted from the total.",
      },
      {
        amount: "$500",
        title: "(A) Geological Study",
        description:
          "This step involves a comprehensive evaluation of your property through detailed analysis of multiple satellite images, topographic assessments, geological studies, and hydrogeological investigations. This multi-layered approach enables us to pinpoint the areas with the highest water potential. Based on these findings, we identify the exact location(s) for field testing —geophysical line(s)—, minimizing the chances of unnecessary secondary tests and maximizing the efficiency of the geophysical survey we will perform.",
      },
      {
        amount: "$700",
        title: "(B) Electrical Resistivity Imaging",
        description:
          "This step represents the actual Field Survey at your property utilizing Electrical Resistivity Imaging (ERI) technology. Imagine it as sending small electrical currents into the ground to map out the subsurface geology. It also includes the comprehensive data processing and professional interpretation of results (sometimes with multiple geologists), detailed report generation, and a clear explanation of the findings to ensure full understanding and practical application. This report will inform you and your well driller about the best place to drill.",
      },
      {
        amount: "$725",
        amountExtra: "extra",
        muted: true,
        title: "(B.1) Possibility (in some cases)",
        description:
          "Properties on a sedimentary geological formation, may require the Schlumberger SEV survey, which is more labor intensive, and therefore requires an additional $725 fee. Contact us for more information or to discover if this feel would be applicable to your property.",
      },
    ],
    note1: "Note 1: These values do not include local taxes.",
    note2: "Note 2: The Geological Study must always be completed before an ERI survey.",
    stepsHeading: "Here is how it usually works:",
    steps: [
      { actor: "[Customer]", text: "You fill out our New Customer Form", link: true },
      { actor: "[AquaFinders]", text: "We contact you to learn more about your needs" },
      { actor: "[Customer]", text: "You sign the contract and make the payment ($500)" },
      {
        actor: "[AquaFinders]",
        text: "We begin with aquifer characterization and a regional well audit, followed by geospatial, topographical, geological, and hydrogeophysical analyses of your property",
      },
      {
        actor: "[AquaFinders]",
        text: "We present your report and provide a clear recommendation on whether or not you should proceed with the next step: the Geophysics (ERI) survey.",
      },
      {
        actor: "[Together]",
        text: "Based on the data collected, we will help decide whether the Geophysical Survey is the right choice for you.",
      },
      { actor: "[Customer]", text: "You make the payment for the second study ($700)" },
      { actor: "[AquaFinders]", text: "We perform the geophysical survey on your property" },
      {
        actor: "[AquaFinders]",
        text: "We deliver the final results to you by e-mail, so you can share it with your well drilling company or builder.",
        final: true,
      },
    ],
    notesTitle: "Important notes:",
    notes: [
      "If everything goes well (pun intented), the process can take as little as three weeks.",
      "The usual turnaround is about five weeks.",
      "We offer rush services for emergency projects (extra fees apply).",
      "Please note that the process and steps will vary depending on your needs, preferences, weather conditions, and conditions of your land.",
      "We offer discounts for larger projects that require multiple studies.",
      "State sales taxes are not included in the prices shown on this website.",
    ],
    otherPricing: "For pricing on our other services, please contact us.",
  },

  contact: {
    formHeading: "Contact Form",
    name: "Your Name",
    email: "Email Address",
    phone: "Phone Number",
    preferred: "Preferred ways of communication:",
    prefEmail: "Email",
    prefCall: "Phone Call",
    prefText: "Text",
    message: "Tell us about your project or questions...",
    captcha: "Captcha",
    submit: "Submit",
    captchaAlert: "Please fill out captcha field!",
    readyTitle: "Ready to Find Your Perfect Well Location?",
    weSpeak: "We speak English / Hablamos Español",
    byAppointment: "Physical address by appointment only.",
    phoneLabel: "Phone",
    phoneHours: "(9-5 PM EST)",
    emailLabel: "Email",
  },

  footer: {
    expertServices: "Expert Groundwater Location Services for",
    serviceArea:
      "North Carolina, South Carolina, Georgia, Tennessee, and Kentucky,",
    nationwide: "with nationwide service available upon special request.",
    contactForm: "Contact Form",
    contactUs: "Contact Us",
    whatsapp: "Contact us via WhatsApp",
    email: "Email",
    trademark: "AquaFinders ™ (Trademark In Progress)",
    allRights: "All rights reserved.",
    resultsVary: "Results will vary based on geological conditions.",
    siteMap: "Site Map",
  },

  faq: {
    title: "Frequently Asked Questions",
    sections: [
      {
        title: "Find the best place to drill a water well",
        intro: null,
        items: [
          {
            q: "1. How much does it cost?",
            a: [
              { type: "process-link" },
              "Our services are divided in two parts: a Geological Study and a Geophysical (Electrical Resistivity Imaging - ERI) survey. The first part costs $500 and it is a pre-requisite for the second study, which costs $700. However, some properties in the USA are set on sedimentary geological formations, which requires the Schlumberger SEV survey, which is more more labor-intensive, and therefore requires the addition of $725 in extra fees. Contact us to learn if your property is or isn't over a sedimentary geological formation.",
              "We also offer discounts for larger projects that require multiple studies.",
            ],
          },
          {
            q: "2. What does it include?",
            a: [
              { type: "usgs" },
              "In practice, we produce two key reports. The first is a Geophysical Analysis of your property, which helps us identify areas with the highest potential for groundwater. Based on those findings, we then conduct a targeted Electrical Resistivity Tomography (ERT) test on the most promising location. The data collected is carefully processed and scientifically analyzed, resulting in the second and final report that includes detailed imaging and a clear assessment of your property's water potential.",
              "Imaging example:",
              { type: "ert-image" },
            ],
          },
          {
            q: "3. What is the process?",
            a: [
              "The best time to hire our services is before contacting a well driller—or even before purchasing a property.",
              "However, if you've already bought the land or a well driller has submitted a permit request, we can still conduct our study. In some cases, this may require additional steps to amend the permit before drilling begins.",
              "Once our contract is active, we start by remotely studying your property, which results in an initial report. We'll then review those findings with you to determine the ideal location for the second phase of the study, which is conducted on-site. During this visit, our team performs in-person tests and collects a wide range of geological and environmental data.",
              "Our expert geologists thoroughly analyze all the data and prepare a detailed final report. This report identifies the exact location with the highest potential for a successful well, allowing you to confidently direct your preferred drilling company to drill in the optimal spot.",
            ],
          },
          {
            q: "4. Is it possible to have negative results?",
            a: [
              "Yes. While we use the best available science and expertise, we cannot guarantee that your property contains water or that the water flow will meet your specific needs. Our goal is to identify the location with the highest potential for a successful well based on the existing geological conditions of your land.",
            ],
          },
          {
            q: "5. Would I get a refund if the result is negative?",
            a: [
              "Unfortunatelly, no. The same process we use to locate the best place to drill is also used to determine if your property has low or even no potential for water. Potentially saving you thousands in future unfruitful wells.",
            ],
          },
          {
            q: "6. Are there any guarantees?",
            a: [
              "We guarantee that we will conduct a professional geophysical and hydrogeophysical study of your property using industry-standard methods and equipment. However, the results we obtain will depend entirely on your property's natural geological conditions. These conditions can affect the test performance, including the depth the equipment can reach, the precision of the readings, and the overall margin of error. Our commitment is to deliver accurate, science-based data to help you make the most informed decision possible.",
            ],
          },
          {
            q: "7. To which ground depth will the study go?",
            a: [
              "We guarantee a professional geophysical and hydrogeophysical study of your property using industry-standard tools and methods. However, the depth and accuracy of our analysis depend on the natural geological conditions of your site. In favorable conditions—which can also be influenced by recent weather—our equipment may allow us to study depths of up to 600 feet. Regardless of conditions, our goal is to provide you with the most accurate, science-based report to guide your well drilling decisions.",
            ],
          },
          {
            q: "8. What size area does the study cover?",
            a: [
              "The standard geophysical study is designed for properties up to 50 acres in size (additional fees apply for larger properties). However, it's important to note that the Electrical Resistivity Tomography (ERT) test is not based on area coverage. Instead, it produces a vertical cross-sectional image of the subsurface along a single straight line. Our standard ERT setup typically spans approximately 1,181 linear feet, with the depth of penetration determined by the local geological conditions.",
            ],
          },
        ],
      },
      {
        title: "Avoiding underground water",
        intro: null,
        items: [
          {
            q: "1. How much does it cost?",
            a: [
              "The standard Electrical Resistivity Tomography (ERT) test cost is $1200 (Twelve Hundred Dollars). Extra fees may apply for properties located outside our standard range of service.",
            ],
          },
        ],
      },
      {
        title: "Resistivity Tests",
        intro:
          "Resistivity tests are a cornerstone of AquaFinders' methodology. By measuring the electrical resistance of the ground, we can map subsurface geological layers and identify water-bearing formations. This non-invasive technique allows us to determine the depth, thickness, and water quality of potential aquifers, providing valuable data for Power Stations, Landfill Projects, and so on.",
        items: [
          {
            q: "1. How much does it cost?",
            a: [
              "The standard Electrical Resistivity Tomography (ERT) test cost is $1200 (Twelve Hundred Dollars). Extra fees may apply for properties located outside our range.",
            ],
          },
        ],
      },
      {
        title: "Studies",
        intro:
          "AquaFinders conduct comprehensive geophysical and hydrogeological studies for environmental projects.",
        items: [
          {
            q: "1. How much does it cost?",
            a: [
              "The standard Electrical Resistivity Tomography (ERT) test cost is $1200 (Twelve Hundred Dollars). Extra fees may apply for properties located outside our standard service range.",
            ],
          },
        ],
      },
    ],
    costLinkPrefix: "For more details, check the ",
    costLinkText: "Pricing and Process",
    costLinkSuffix: " page.",
    usgsPrefix:
      "Our work combines the analysis of existing geological data, geospatial imaging, historical well records, ",
    usgsText: "USGS",
    usgsSuffix:
      " topographic maps, and local climate patterns to develop a comprehensive understanding of your property's water potential.",
    ertAlt: "Example of an Electrical Resistivity Tomography (ERT) report",
    importantTitle: "Important:",
    importantText:
      "We do not drill water wells. We study the subsurface conditions of your property to best prepare well drillers and construction companies to make informed decisions.",
  },

  gallery: {
    title: "Gallery",
    imageAlts: [
      "AquaFinders field survey — geophysical resistivity work (1)",
      "AquaFinders field survey — geophysical resistivity work (2)",
      "AquaFinders field survey — geophysical resistivity work (3)",
      "AquaFinders field survey — geophysical resistivity work (4)",
      "AquaFinders field survey — geophysical resistivity work (5)",
      "AquaFinders field survey — geophysical resistivity work (6)",
      "AquaFinders field survey — geophysical resistivity work (7)",
      "AquaFinders field survey — geophysical resistivity work (8)",
      "AquaFinders field survey — geophysical resistivity work (9)",
      "Sample resistivity imaging cross-section",
    ],
  },

  request: {
    title: "Start now",
    intro:
      "Please let us know about your project and we will contact you to discuss the next steps.",
    iframeTitle: "AquaFinders - Initial Contact",
    loading: "Loading…",
  },

  success: {
    title: "Thank You for Contacting Us!",
    message:
      "Your message has been successfully submitted. Our team will get back to you as soon as possible.",
    backHome: "Back to Home",
  },

  sitemap: {
    title: "Site Map",
    links: {
      home: "Home",
      ourServices: "Our Services",
      aboutUs: "About Us",
      contactUs: "Contact Us",
      startNow: "Start now",
      pricing: "Process and Pricing",
      faq: "FAQ",
      otherLanguage: "Spanish Version",
      formSubmission: "Form Submission",
      siteMap: "Site Map",
    },
  },

  notFound: {
    title: "Page not found",
    text: "Sorry, the page you are looking for does not exist.",
    backHome: "Back to Home",
  },
};

export default en;
